import React,{useState} from 'react';
import {get} from '../../api/coreApi';

type DigitalOceanBilling={
  enabled:boolean;connected:boolean;currency:'USD';
  scope:'digitalocean_account_all_products';period:string;
  reason?:string|null;recordedAt:string|null;
  invoicePreviewUsd:number|null;historicalInvoiceUsd:number|null;
  monthToDateAccountUsageUsd:number|null;
  accountBalanceUsd:number|null;accountGeneratedAt:string|null;
  insightsDailyEstimatedUsd:number|null;insightsItems:number;
  insightsComplete:boolean|null;
  components:{balance:string;preview:string;historicalInvoice:string;insights:string};
  note:string;
};
const money=(amount:number|null)=>{
  if(amount===null||!Number.isFinite(Number(amount)))return 'Not available';
  return Number(amount).toLocaleString('en-US',{style:'currency',currency:'USD',
    minimumFractionDigits:2,maximumFractionDigits:2});
};
export const DigitalOceanBillingPanel:React.FC<{period:string}>=({period})=>{
  // Do not load provider-level spending automatically for ordinary instance
  // overseers. The server requires a verified PLATFORM billing administrator.
  const [data,setData]=useState<DigitalOceanBilling|null>(null);
  const [loadedPeriod,setLoadedPeriod]=useState('');
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const load=async()=>{
    setLoading(true);setError('');setData(null);
    try{
      const response=await get<{success:boolean;billing:DigitalOceanBilling}>(
        '/ai/platform/digitalocean-billing',{params:{month:period}},
      );
      setData(response.billing);
      setLoadedPeriod(period);
    }catch(cause){
      setLoadedPeriod(period);
      const message=cause instanceof Error?cause.message:'Unable to load provider billing.';
      setError(message);
    }finally{setLoading(false);}
  };
  const latest=loadedPeriod===period?data:null;
  return <section className="ezee-economics__panel" aria-label="Read-only DigitalOcean account billing">
    <h4>DigitalOcean billing connection</h4>
    <p className="ezee-economics__scope"><strong>Platform administrators only · Account-wide data</strong>
      {' '}This connection is read-only and cannot allocate shared hosting, database or AI charges to a particular Elite Core instance. Credentials are configured on the server, never entered in Command Centre.</p>
    <div className="ezee-economics__actions" style={{marginTop:12}}>
      <button type="button" onClick={()=>void load()} disabled={loading}>
        {loading?'Connecting…':loadedPeriod===period?'Refresh account billing':'Load DigitalOcean billing'}
      </button>
      <small>Requires developer-enabled billing:read token and platform administrator role.</small>
    </div>
    {error&&loadedPeriod===period?<p role="alert" className="ezee-economics__error">{error}</p>:null}
    {latest?<>
      <p className="ezee-economics__muted"><strong>Connection:</strong> {latest.enabled
        ? latest.connected?'Read-only billing API responded':'Enabled, but no billing data available'
        : 'Disabled by developers'}
        {' '}· Period: {latest.period} · Last checked: {latest.recordedAt?new Date(latest.recordedAt).toLocaleString():'Never'}
      </p>
      <div className="ezee-economics__metrics">
        {[
          {label:'Account month-to-date usage',amount:latest.monthToDateAccountUsageUsd,
            detail:'All DigitalOcean products, not Ezee'},
          {label:'Account invoice '+(latest.components.historicalInvoice==='not_applicable_to_current_period'?'preview':'summary'),
            amount:latest.components.historicalInvoice==='not_applicable_to_current_period'
              ?latest.invoicePreviewUsd:latest.historicalInvoiceUsd,
            detail:'Account-wide invoice; not solely Ezee inference'},
          {label:'Daily billing insights sum',amount:latest.insightsDailyEstimatedUsd,
            detail:latest.insightsComplete===false?'Partial pagination; do not treat as total':'Nightly estimate, not final invoice'},
          {label:'Account billing balance',amount:latest.accountBalanceUsd,
            detail:'Not the same as inference prepaid balance'},
        ].map(row=><div className="ezee-economics__metric" key={row.label}>
          <span>{row.label}</span><strong>{money(row.amount)}</strong><small>{row.detail}</small>
        </div>)}
      </div>
      <p className="ezee-economics__muted">API components — balance: {latest.components.balance};
        invoice preview: {latest.components.preview}; historical invoices: {latest.components.historicalInvoice}; daily insights: {latest.components.insights}.
        {' '}{latest.note}</p>
    </>:null}
  </section>;
};
